/**
 * useHousingJourney — Canonical single source of truth for the entire Project Workspace.
 *
 * Design:
 * - Module-level cache (30s TTL) shared across all workspace tabs
 * - Fetches /api/housing/my-journey + /api/housing/evidence in parallel
 * - Exposes `refresh()` for manual invalidation after save/upload/review actions
 * - `invalidateHousingCache()` export for optimistic invalidation
 *
 * All workspace tabs must use this hook instead of independent fetches.
 * This eliminates drift between tabs caused by independent fetches returning different versions.
 */
import { useState, useEffect, useCallback } from "react";
import client from "@/api/client";

// ── Module-level cache ───────────────────────────────────────────────────────
let _journey     = null;
let _evidence    = null;
let _cachedAt    = 0;
const CACHE_TTL  = 30_000; // 30 seconds

// ── Public cache invalidation (call after state-changing actions) ────────────
export function invalidateHousingCache() {
  _journey  = null;
  _evidence = null;
  _cachedAt = 0;
}

// ── Derived evidence coverage from raw evidence response ────────────────────
function deriveEvidenceCoverage(evidenceData) {
  const items    = evidenceData?.items || [];
  const coverage = evidenceData?.coverage || {};
  const summary  = evidenceData?.summary  || {};

  // Set of params with at least 'uploaded' status (usable for MRV gate check)
  const uploadedParams = new Set(
    items
      .filter(e => ["uploaded","under_review","verified"].includes(e.verification_status))
      .map(e => e.linked_parameter)
  );
  // Verified-only set (for workflow gate)
  const verifiedParams = new Set(
    items
      .filter(e => e.verification_status === "verified")
      .map(e => e.linked_parameter)
  );

  return {
    items,
    uploadedParams,
    verifiedParams,
    critical_covered: coverage.critical_covered || 0,
    critical_total:   coverage.critical_total   || 7,
    coverage_pct:     coverage.coverage_pct     || 0,
    covered_params:   coverage.covered_params   || [],
    missing_params:   coverage.missing_params   || [],
    total_verified:   summary.verified          || 0,
    total_items:      summary.total             || 0,
    // Convenience: is a given param covered?
    has: (param) => uploadedParams.has(param),
    isVerified: (param) => verifiedParams.has(param),
  };
}

// ── Canonical hook ─────────────────────────────────────────────────────────
export function useHousingJourney({ autoFetch = true } = {}) {
  const [journey,  setJourney]  = useState(_journey);
  const [evidence, setEvidence] = useState(_evidence);
  const [loading,  setLoading]  = useState(!_journey);
  const [error,    setError]    = useState(null);

  const fetchAll = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && _journey && (now - _cachedAt) < CACHE_TTL) {
      setJourney(_journey);
      setEvidence(_evidence);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [jRes, eRes] = await Promise.all([
        client.get("/housing/my-journey"),
        client.get("/housing/evidence"),
      ]);
      _journey  = jRes.data;
      _evidence = eRes.data;
      _cachedAt = Date.now();
      setJourney(_journey);
      setEvidence(_evidence);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Manual refresh — invalidates cache and re-fetches
  const refresh = useCallback(() => {
    invalidateHousingCache();
    fetchAll(true);
  }, [fetchAll]);

  useEffect(() => {
    if (autoFetch) fetchAll();
  }, [autoFetch, fetchAll]);

  const evidenceCoverage = deriveEvidenceCoverage(evidence);

  return {
    // Core data
    journey,
    evidence,
    loading,
    error,
    refresh,

    // Derived: project identity
    assessmentId:    journey?.assessment_id,
    calcVersion:     journey?.version,
    dataSource:      journey?.data_source,
    isDbBacked:      journey?.data_source === "db_backed",

    // Derived: project state
    workflowStatus:  journey?.workflow_status || journey?.status || "draft",
    isVerified:      (journey?.workflow_status || journey?.status) === "verified",

    // Derived: carbon pathway (from backend — single source of truth)
    carbonPathway:   journey?.carbon_pathway,
    pathways:        journey?.carbon_pathway?.pathways || {},

    // Derived: financial data
    sensitivity:     journey?.sensitivity_analysis,
    financialModel:  journey?.financial_model,
    financingReady:  journey?.financing_readiness,

    // Derived: evidence coverage
    evidenceCoverage,

    // Shortcut project slices
    org:     journey?.organization || {},
    proj:    journey?.project      || {},
    demo:    journey?.demo_unit    || {},
    prog:    journey?.programme    || {},
    kpis:    journey?.kpis         || {},
    sdgTags: journey?.sdg_tags     || [],
    actions: journey?.actions      || [],
    bm:      journey?.industry_benchmark || {},
    wtm:     journey?.what_this_means    || {},
    adminNote: journey?.admin_completion_note,
  };
}
